'use client';

import {
  CssBaseline,
  Experimental_CssVarsProvider as CssVarsProvider,
  experimental_extendTheme as extendTheme,
  useColorScheme,
  type PaletteMode,
} from '@mui/material';
import { AppRouterCacheProvider } from '@mui/material-nextjs/v14-appRouter';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  createContext,
  useEffect,
  useMemo,
  type PropsWithChildren,
} from 'react';
import type {} from '@mui/material/themeCssVarsAugmentation'; //! Do not remove this import otherwise you will get type errors while making a production build

import { themeConfig } from '@/configs/themeConfig';
import { useNonProjectDal } from '@/contexts/useNonProjectDal';
import { PreferencesKey } from '@/data/dal/preferencesKey';
import { theme as defaultCoreTheme } from '@/theme';

export const ThemeContext = createContext<{
  theme: PaletteMode;
  changeTheme: (theme: PaletteMode) => void;
}>({ theme: 'light', changeTheme: () => {} });

export const ThemeProvider = (
  props: PropsWithChildren<{ isAuthenticated: boolean }>
) => {
  if (props.isAuthenticated) {
    return <AuthThemeProvider>{props.children}</AuthThemeProvider>;
  }

  return (
    <InnerThemeProvider currentMode={'light'} onChangeTheme={() => {}}>
      {props.children}
    </InnerThemeProvider>
  );
};

const AuthThemeProvider = (props: PropsWithChildren) => {
  const { nonProjectDal: dal } = useNonProjectDal();
  const { data: prefResponse } = dal.preferences.useUserPref(
    PreferencesKey.Preferences
  );

  const currentMode: PaletteMode = prefResponse?.value?.theme || 'light';

  return (
    <InnerThemeProvider
      currentMode={currentMode}
      onChangeTheme={(t) => {
        void dal.preferences.updateUserPref(PreferencesKey.Preferences, {
          ...prefResponse?.value,
          theme: t,
        });
      }}
    >
      {props.children}
    </InnerThemeProvider>
  );
};

const InnerThemeProvider = (
  props: PropsWithChildren<{
    currentMode: PaletteMode;
    onChangeTheme: (t: PaletteMode) => void;
  }>
) => {
  // Merge the primary color scheme override with the core theme
  const theme = useMemo(() => {
    const coreTheme = defaultCoreTheme();
    return extendTheme(coreTheme);
  }, []);

  return (
    <AppRouterCacheProvider
      options={{
        prepend: true,
      }}
    >
      <CssVarsProvider
        theme={theme}
        defaultMode={'light'}
        modeStorageKey={`${themeConfig.templateName.toLowerCase().split(' ').join('-')}-mui-template-mode`}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <ThemeWrapper
            theme={props.currentMode}
            onChangeTheme={props.onChangeTheme}
          >
            <CssBaseline />
            {props.children}
          </ThemeWrapper>
        </LocalizationProvider>
      </CssVarsProvider>
    </AppRouterCacheProvider>
  );
};

const ThemeWrapper = ({
  children,
  theme,
  onChangeTheme,
}: PropsWithChildren<{
  theme: PaletteMode;
  onChangeTheme: (t: PaletteMode) => void;
}>) => {
  const { mode, setMode } = useColorScheme();

  useEffect(() => {
    setMode(theme);
  }, [setMode, theme]);

  return (
    <ThemeContext.Provider
      value={{
        theme: mode === 'dark' ? 'dark' : 'light',
        changeTheme: (t) => {
          setMode(t);
          onChangeTheme(t);
        },
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
